import axios from '../../utils/axios';
import { objectToFormData } from '../utils';

// GET
// /ProductCatalog
export async function getCatalogData() {
  const response = await axios.get('/ProductCatalog');
  if (response.data?.products?.length) {
    const vendorHash = response.data.vendors?.reduce((acc, vendor) => { acc[vendor.value] = vendor.label; return acc; }, {}) ?? {};
    const categoryHash = response.data.agoraCategories?.reduce((acc, cat) => { acc[cat.id] = cat.name; return acc; }, {}) ?? {};
    const subcategoryHash = response.data.agoraSubcategories?.reduce((acc, subcat) => { acc[subcat.id] = subcat.name; return acc; }, {}) ?? {};
    const editLockedHash = response.data.editLockedCategories?.reduce((acc, catId) => { acc[catId] = true; return acc; }, {}) ?? {};
    const editOrderGuideLockedHash = response.data.editOrderGuideLockedCategories?.reduce((acc, catId) => { acc[catId] = true; return acc; }, {}) ?? {};
    response.data.products = response.data.products.map(prod => {
      prod.vendor = vendorHash[prod.vendorId];
      prod.editLocked = editLockedHash[prod.vendorId];
      prod.orderGuideLocked = editOrderGuideLockedHash[prod.vendorId];
      prod.category = categoryHash[prod.categoryId];
      prod.subcategory = subcategoryHash[prod.subcategoryId];
      prod.vendorItemIds = prod.vendorItemIds?.split(',').map(id => id.trim());
      prod.otherCategories = prod.otherCategories?.map(otherCat => ({ category: categoryHash[otherCat.categoryId], subcategory: subcategoryHash[otherCat.subcategoryId] }));      
      return prod;
    }).sort((a, b) => a.description.localeCompare(b.description));
  }
  return response;
}

// POST
// /GenerateCatalogSuggestion description
export const getCatalogSuggestion = async (description) => {
  return axios.post('/GenerateCatalogSuggestion', description, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
};

// POST /Api/SuggestCatalogCombinations
export async function suggestCatalogCombinations(newItem) {
  const response = await axios.post('/SuggestCatalogCombinations', newItem);
  return response;
}

export function buildCombineSuggestionPayload(parentData, productData, categories) {
  return {
    description: parentData?.description,
    manufacturer: productData?.manufacturer,
    manufacturerId: productData?.manufacturerId,
    vendorItemDescription: productData?.vendorItemDescription,
    categories: categories?.map((cat) => ({
      categoryId: cat.agoraCategoryId,
      subcategoryId: cat.agoraSubcategoryId,
    })),
  };
}
// GET
// /ProductOrdered productId
export async function getProductOrderedData(productId, vendorItemId, vendorId) {
  const response = await axios.get(`/ProductOrdered?productId=${productId}&vendorItemId=${vendorItemId}&vendorId=${vendorId}`);
  return response;
}

// GET
// /ProductHistory productId
export async function getProductHistoryData(productId, vendorItemId, vendorId) {
  const response = await axios.get(`/ProductHistory?productId=${productId}&vendorItemId=${vendorItemId}&vendorId=${vendorId}`);
  return response;
}
// /Api/GetProductDetails
export async function getProductDetailsData(productId, flag) {
  const response = await axios.get(`/GetProductDetails?id=${productId}&categoriesAndVendors=${flag}`);
  return response;
}
// GET
// /ProductOrderGuideData productId agoraCategoryId
export async function getProductOrderGuideData(productId, agoraCategoryId) {
  const response = await axios.get(`/ProductOrderGuideData?productId=${productId}&agoraCategoryId=${agoraCategoryId}`);
  return response;
}

// GET
// /DownloadCatalog
export async function getDownloadCatalogData() {
  const response = await axios.get('/DownloadCatalog');
  return response;
}

// GET
// /FetchVendorProduct vendorId item, uom
export async function fetchVendorProductData(vendorId, item, uom) {
  const response = await axios.get(`/FetchVendorProduct?vendorId=${vendorId}&item=${item}&uom=${uom}`);
  return response;
}

// GET
// /PossibleProductUOMs vendorId item
export async function possibleProductUOMsData(vendorId, item) {
  const response = await axios.get(`/PossibleProductUOMs?vendorId=${vendorId}&item=${item}`);
  return response;
}

// POST
// /DeleteProducts productIds
export async function deleteProductsData(productIds) {
  const response = await axios.post('/DeleteProducts', objectToFormData({ productIds }));
  return response;
}

// POST
// /DeactivateProduct productId deactivate
export async function deactivateProductData(productId, deactivate) {
  const response = await axios.post(`/DeactivateProduct?productId=${productId}&deactivate=${deactivate}`);
  return response;
}

export async function deleteProductItem(itemId) {
  const response = await axios.post(`/DeleteProductItem?itemId=${itemId}`);
  return response;
}

export async function combineProductData({ connectTo, connectProductIds }) {
  const response = await axios.post(`/CombineProducts`, objectToFormData({ connectTo, connectProductIds }));
  return response;
}

export async function postAddProductImage(picture) {
  const formData = new FormData();
  formData.append('picture', picture);
  const response = await axios.post(`/AddProductImage`, formData);
  return response;
}


export async function postBulkAddProductImage({ siteId, isRental, vendorId, zipFile }) {
    const formData = new FormData();
    formData.append('zipFile', zipFile);
    formData.append('vendorId', vendorId);
    formData.append('siteId', siteId);
    formData.append('isRental', isRental);
    const response = await axios.post(`/BulkAddProductImage`, formData);
  return response;
}


// POST
export async function addProductToOrderGuides(permittedFacilities, productId, agoraCategoryId, agoraSubcategoryId) {
  const response = await axios.post(
    `/AddProductToOrderGuides?productId=${productId}&agoraCategoryId=${agoraCategoryId}`,
    objectToFormData({
      permittedOrderGuideIds: permittedFacilities,
      agoraSubcategoryId,
    })
  );
  return response;
}

export async function addAdditionalCategories(items, agoraCategoryId) {
  return axios.post(`/AddAdditionalCategories?agoraCategoryId=${agoraCategoryId}`, objectToFormData({ items }));
}

export async function productsFormData({ parentData, productData, categories, vendorId, vendorValue, ppdFacilities }) {
  const { description, notes, files } = parentData;

  const { agoraCategoryId, agoraSubcategoryId } = categories[0];

  const productCategories =
    categories.length > 1
      ? categories?.slice(1).map((category) => ({
          AgoraCategoryId: category.agoraCategoryId,
          AgoraSubcategoryId: category.agoraSubcategoryId,
        }))
      : [];

  const formattedProductData = [productData].map((product) => ({
    vendorId: vendorValue,
    vendorItemId: vendorId,
    price: product.price,
    uomAmount: product.uomAmount,
    uomType: product?.uomType || product.uom,
    baseUOMAmount: product.baseUOMAmount,
    baseMinUOMAmount: product.baseMinUOMAmount,
    baseUOMType: product.baseUOMType,
    manufacturer: product.manufacturer,
    manufacturerId: product.manufacturerId,
    isPPD: product.isPPD,
    pictures: product.pictureUrls,
    splittable: !!product.uomAmount2,
    vendorItemLink: product.vendorItemLink,
    vendorItemDescription: product.vendorItemDescription,
    ppdFacilities,
    productDetailUOM: product.uomAmount2
      ? {
          price: product.price2,
          uomAmount: product.uomAmount2,
          uomType: product.uomType2,
        }
      : {},
  }));

  const submitData = {
    Id: 0,
    Description: description,
    AgoraCategoryId: agoraCategoryId,
    AgoraSubcategoryId: agoraSubcategoryId,
    Notes: notes ?? '',
    Files: files,
    ProductCategories: productCategories,
    ProductDetails: formattedProductData,
  };

  const response = await axios.post(`/ProductsForm`, objectToFormData(submitData));
  return response;
}

export async function updateProductFormData({
  categories,
  allItems,
  productId,
  description,
  notes,
  files,
  fileIdsToDelete,
  agoraCategoryId,
  agoraSubcategoryId,
}) {
  const additionalCategories = categories.slice(1);
  const submitData = {
    Id: productId,
    Description: description,
    AgoraCategoryId: agoraCategoryId,
    AgoraSubcategoryId: agoraSubcategoryId,
    Notes: notes,
    ProductCategories: additionalCategories,
    ProductDetails: allItems,
    Files: files,
    FileIdsToDelete: fileIdsToDelete,
  };
  const response = await axios.post(`/ProductsForm`, objectToFormData(submitData));
  return response;
}

//GET /Api/DuplicateItem\ vendorId vendorItemId itemId
export async function duplicateItemData(vendorId, vendorItemId, itemId) {
  const response = await axios.get(`/DuplicateItem?vendorId=${vendorId}&vendorItemId=${vendorItemId}&itemId=${itemId}`);
  return response;
}
// /Api/SplitProduct
export async function splitProductData({ productId, itemsToSplit }) {
  const submitData = {
    productId,
    itemsToSplit,
  };

  const response = await axios.post(`/SplitProduct`, objectToFormData(submitData));
  return response;
}

export async function getAllSKUs() {
  const response = await axios.get(`/ViewAllSKUs`);
  return response;
}

// GET
// /OpenProductFile productFileId
export async function openProductFile(productFileId) {
  const response = await axios.get(
    `/OpenProductFile?productFileId=${productFileId}`,
    { responseType: 'blob' }
  );
  return response;
}
